import { type ReactNode, useCallback, useMemo } from 'react';
import {
  ApiClientProvider,
  type AuthenticatedRequestFailureHandler,
} from '../../api';
import { MeSessionContextProvider, teamIdContext } from './team.context';
import { useMeQuery } from './team.queries';
import { resolveSessionTeamId } from './team.session.utils';

export type DirectTokenSession = {
  teamId: string;
  userId: string;
};

function DirectTokenSessionContent({
  apiBaseUrl,
  children,
  getToken,
  onAuthenticatedRequestFailure,
  session,
}: {
  apiBaseUrl?: string | null;
  children: ReactNode;
  getToken: () => Promise<string>;
  onAuthenticatedRequestFailure?: AuthenticatedRequestFailureHandler;
  session: DirectTokenSession;
}) {
  const me = useMeQuery({
    allowTeamFallback: false,
    userId: session.userId,
    requestedTeamId: session.teamId,
  });
  const meTeamId = me.data ? (me.data.team?.id ?? null) : undefined;
  const { isBootstrappingTeamId, resolvedTeamId } = resolveSessionTeamId({
    userId: session.userId,
    meTeamId,
    hasMeError: !!me.error,
  });
  const teamIdValue = useMemo(
    () => ({
      teamId: resolvedTeamId,
      storedTeamId: undefined,
      setTeamId: () => undefined,
    }),
    [resolvedTeamId]
  );

  return (
    <teamIdContext.Provider value={teamIdValue}>
      <ApiClientProvider
        allowOpaqueAccessToken
        baseUrlOverride={apiBaseUrl}
        getToken={getToken}
        onAuthenticatedRequestFailure={onAuthenticatedRequestFailure}
        teamId={resolvedTeamId}
        isTeamIdFetching={isBootstrappingTeamId}
      >
        <MeSessionContextProvider
          isBootstrappingTeamId={isBootstrappingTeamId}
          me={me}
          realUserId={session.userId}
          setTeamId={teamIdValue.setTeamId}
        >
          {children}
        </MeSessionContextProvider>
      </ApiClientProvider>
    </teamIdContext.Provider>
  );
}

export function DirectTokenSessionProvider({
  accessToken,
  apiBaseUrl,
  children,
  onAuthenticatedRequestFailure,
  session,
}: {
  accessToken: string;
  apiBaseUrl?: string | null;
  children: ReactNode;
  onAuthenticatedRequestFailure?: AuthenticatedRequestFailureHandler;
  session: DirectTokenSession;
}) {
  const getToken = useCallback(async () => accessToken, [accessToken]);

  return (
    <ApiClientProvider
      allowOpaqueAccessToken
      baseUrlOverride={apiBaseUrl}
      getToken={getToken}
      onAuthenticatedRequestFailure={onAuthenticatedRequestFailure}
      teamId={undefined}
      isTeamIdFetching={false}
    >
      <DirectTokenSessionContent
        apiBaseUrl={apiBaseUrl}
        getToken={getToken}
        onAuthenticatedRequestFailure={onAuthenticatedRequestFailure}
        session={session}
      >
        {children}
      </DirectTokenSessionContent>
    </ApiClientProvider>
  );
}
